/**
 * Resilience utilities for retry logic and timeout handling
 */

import { SDK_VERSION } from '../../version.js';

export interface RetryConfig {
  maxRetries?: number;        // Default: 3
  initialDelay?: number;      // Default: 1000ms
  maxDelay?: number;          // Default: 30000ms
  backoffMultiplier?: number; // Default: 2
  retryableErrors?: string[]; // Default: ['ECONNREFUSED', 'ETIMEDOUT', 'ENOTFOUND']
  onRetry?: (attempt: number, error: Error) => void;
}

const DEFAULT_RETRY_CONFIG: Required<Omit<RetryConfig, 'onRetry'>> = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 30000,
  backoffMultiplier: 2,
  retryableErrors: ['ECONNREFUSED', 'ETIMEDOUT', 'ENOTFOUND'],
};

/**
 * Retry a fetch request with exponential backoff
 * 
 * @param url - Request URL
 * @param options - Fetch options
 * @param retryConfig - Retry configuration
 * @returns Response from fetch
 * 
 * @example
 * ```typescript
 * const response = await fetchWithRetry(
 *   'https://api.example.com/data',
 *   { method: 'POST', body: JSON.stringify(data) },
 *   { maxRetries: 5, initialDelay: 500 }
 * );
 * ```
 */
export async function fetchWithRetry(
  url: string,
  options: RequestInit,
  retryConfig: RetryConfig = {}
): Promise<Response> {
  const {
    maxRetries = DEFAULT_RETRY_CONFIG.maxRetries,
    initialDelay = DEFAULT_RETRY_CONFIG.initialDelay,
    maxDelay = DEFAULT_RETRY_CONFIG.maxDelay,
    backoffMultiplier = DEFAULT_RETRY_CONFIG.backoffMultiplier,
    retryableErrors = DEFAULT_RETRY_CONFIG.retryableErrors,
    onRetry,
  } = retryConfig;

  let lastError: Error | null = null;
  let delay = initialDelay;

  // Inject SDK version header (caller-provided headers can override)
  options = { ...options, headers: { 'X-Morph-SDK-Version': SDK_VERSION, ...options.headers } };

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);
      
      // Retry on 429 (rate limit) or 503 (service unavailable)
      if (response.status === 429 || response.status === 503) {
        if (attempt < maxRetries) {
          // Check for Retry-After header
          const retryAfter = response.headers.get('Retry-After');
          const waitTime = retryAfter 
            ? parseInt(retryAfter) * 1000 
            : Math.min(delay, maxDelay);
          
          const error = new Error(`HTTP ${response.status}: Retrying after ${waitTime}ms`);
          if (onRetry) {
            onRetry(attempt + 1, error);
          }
          
          await sleep(waitTime);
          delay *= backoffMultiplier;
          continue;
        }
      }

      return response;
    } catch (error) {
      lastError = error as Error;
      
      // Check if error is retryable
      const isRetryable = retryableErrors.some(errType => 
        lastError?.message?.includes(errType)
      );

      if (!isRetryable || attempt === maxRetries) {
        throw lastError;
      }

      // Exponential backoff
      const waitTime = Math.min(delay, maxDelay);
      if (onRetry) {
        onRetry(attempt + 1, lastError);
      }
      
      await sleep(waitTime);
      delay *= backoffMultiplier;
    }
  }

  throw lastError || new Error('Max retries exceeded');
}

/**
 * Add timeout to any promise
 * 
 * @param promise - Promise to wrap with timeout
 * @param timeoutMs - Timeout in milliseconds
 * @param errorMessage - Optional custom error message
 * @returns Promise that rejects if timeout is reached
 * 
 * @example
 * ```typescript
 * const result = await withTimeout(
 *   fetchData(),
 *   5000,
 *   'Data fetch timed out'
 * );
 * ```
 */
export async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  errorMessage?: string
): Promise<T> {
  let timeoutId: NodeJS.Timeout | number;
  
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(errorMessage || `Operation timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  try {
    const result = await Promise.race([promise, timeoutPromise]);
    clearTimeout(timeoutId!);
    return result;
  } catch (error) {
    clearTimeout(timeoutId!);
    throw error;
  }
}

/**
 * Sleep for specified milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Unified error type for all tools
 */
export class MorphError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode?: number,
    public retryable: boolean = false
  ) {
    super(message);
    this.name = 'MorphError';
  }
}


